import { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from "recharts";

const COLORS = ["#6366f1", "#f59e0b", "#10b981", "#f43f5e"];
const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

// Build default 3 months from today
function defaultMonths() {
  const today = new Date();
  const result = [];
  for (let i = 0; i < 3; i++) {
    let m = today.getMonth() + 1 - i;
    let y = today.getFullYear();
    if (m <= 0) { m += 12; y -= 1; }
    result.push({ year: y, month: m });
  }
  return result;
}

function monthKey(y, m) {
  return `${y}-${String(m).padStart(2, "0")}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const totalRecords = payload.reduce((sum, e) => {
    if (e.value == null) return sum;
    return sum + (e.payload[`${e.name}__records`] ?? 0);
  }, 0);
  return (
    <div style={{
      background: "#f8fafc", border: "1px solid #e2e8f0",
      borderRadius: 8, padding: "10px 14px", minWidth: 210
    }}>
      <p style={{ color: "#94a3b8", marginBottom: 6, fontSize: 12 }}>
        Día {label}
        {totalRecords > 0 && (
          <span style={{
            marginLeft: 6, background: "#e0e7ff", color: "#4338ca",
            borderRadius: 4, padding: "1px 6px", fontSize: 11, fontWeight: 700
          }}>
            {totalRecords} {totalRecords === 1 ? "venta" : "ventas"}
          </span>
        )}
      </p>
      {payload.map((entry, i) => {
        if (entry.value == null) return null;
        const records = entry.payload[`${entry.name}__records`];
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 3 }}>
            <span style={{ color: entry.color, fontSize: 12 }}>
              {entry.name}
              {records != null && <span style={{ color: "#94a3b8", fontSize: 10, marginLeft: 4 }}>({records})</span>}
            </span>
            <span style={{ color: "#1e293b", fontWeight: 600, fontSize: 13 }}>
              ${entry.value.toLocaleString("en-US", { minimumFractionDigits: 0 })}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default function DailySalesChart({ token, apiBase = "/api" }) {
  const today = new Date();
  const [selectedMonths, setSelectedMonths] = useState(defaultMonths);
  const [selectedStore, setSelectedStore] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adjustment, setAdjustment] = useState(null);
  // Picker state
  const [pickerYear, setPickerYear] = useState(today.getFullYear());
  const [pickerMonth, setPickerMonth] = useState(today.getMonth() + 1);
  const [showPicker, setShowPicker] = useState(false);

  const fetchData = async (months, storeId) => {
    setLoading(true);
    setError(null);
    try {
      const monthsStr = months.map(({ year, month }) => monthKey(year, month)).join(",");
      const params = new URLSearchParams({ months: monthsStr });
      if (storeId) params.append("store_id", storeId);
      const url = `${apiBase}/reports/daily-sales-comparison?${params.toString()}`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedMonths, selectedStore);
    // Fetch adjustment for current month
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${apiBase}/reports/projection-adjustment?year=${today.getFullYear()}&month=${today.getMonth() + 1}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setAdjustment(d))
      .catch(() => {});
  }, []);

  const monthKeys = data ? Object.keys(data.months) : [];

  const chartData = useMemo(() => {
    if (!data) return [];
    const rows = [];
    for (let day = 1; day <= 31; day++) {
      const row = { day };
      let hasData = false;
      monthKeys.forEach(mk => {
        const dayData = data.months[mk]?.days[day];
        row[mk] = dayData?.total_usd ?? null;
        row[`${mk}__records`] = dayData?.records ?? null;
        if (row[mk] !== null) hasData = true;
      });
      if (hasData) rows.push(row);
    }
    return rows;
  }, [data]);

  const addMonth = () => {
    const key = monthKey(pickerYear, pickerMonth);
    const alreadyIn = selectedMonths.some(m => monthKey(m.year, m.month) === key);
    if (alreadyIn || selectedMonths.length >= 4) return;
    const next = [...selectedMonths, { year: pickerYear, month: pickerMonth }];
    setSelectedMonths(next);
    setShowPicker(false);
    fetchData(next, selectedStore);
  };

  const removeMonth = (key) => {
    if (selectedMonths.length <= 1) return;
    const next = selectedMonths.filter(m => monthKey(m.year, m.month) !== key);
    setSelectedMonths(next);
    fetchData(next, selectedStore);
  };

  if (error) return null;

  const firstKey = monthKeys[0];
  const firstMonth = data?.months?.[firstKey];
  const secondKey = monthKeys[1];
  const peakDay = firstMonth?.peak_day;
  const varPct = (firstMonth && secondKey && data.months[secondKey]?.total_month_usd > 0)
    ? Math.round(((firstMonth.total_month_usd - data.months[secondKey].total_month_usd) / data.months[secondKey].total_month_usd) * 100)
    : null;

  // --- Projection for current month ---
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();
  // Backend keys months by label ("March 2026"), not by "YYYY-MM"
  // Use the year/month fields inside each month's data to identify current month
  const currentMonthKey = data
    ? monthKeys.find(mk => data.months[mk]?.year === todayYear && data.months[mk]?.month === todayMonth)
    : null;
  const currentMonthInChart = !!currentMonthKey;
  const projection = useMemo(() => {
    if (!data || !currentMonthKey) return null;
    const md = data.months[currentMonthKey];
    if (!md) return null;
    const daysInMonth = new Date(todayYear, todayMonth, 0).getDate();
    const rawTotal = Object.entries(md.days || {})
      .filter(([d]) => parseInt(d) <= todayDay)
      .reduce((sum, [, v]) => sum + (v.total_usd || 0), 0);
    if (rawTotal <= 0) return null;
    const adjAmount = adjustment?.adjustment_amount ?? 0;
    const adjDetails = adjustment?.adjustments ?? [];
    const currentTotal = rawTotal + adjAmount;
    const dailyAvg = currentTotal / todayDay;
    const projectedTotal = dailyAvg * daysInMonth;
    return {
      rawTotal,
      currentTotal,
      projectedTotal,
      dailyAvg,
      adjAmount,
      adjDetails,
      daysElapsed: todayDay,
      daysInMonth,
      progress: Math.round((todayDay / daysInMonth) * 100),
    };
  }, [data, currentMonthInChart, adjustment]);

  const yearOptions = [today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2];

  return (
    <div style={{
      background: "#ffffff", border: "1px solid #e2e8f0",
      borderRadius: 14, padding: "24px 20px", fontFamily: "inherit"
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
            📈 Ventas Diarias — Comparación de Meses
          </h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>
            Selecciona hasta 4 meses para comparar
          </p>
        </div>
        {/* Store selector */}
        {data?.stores?.length > 0 && (
          <select value={selectedStore}
            onChange={e => { setSelectedStore(e.target.value); fetchData(selectedMonths, e.target.value); }}
            style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b", padding: "6px 12px", fontSize: 13, cursor: "pointer", outline: "none" }}>
            <option value="">Todas las sedes</option>
            {data.stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      {/* Month chips + add button */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        {selectedMonths.map(({ year, month }, idx) => {
          const key = monthKey(year, month);
          const color = COLORS[idx];
          return (
            <div key={key} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: color + "18", border: `1.5px solid ${color}`,
              borderRadius: 20, padding: "4px 10px 4px 12px",
              fontSize: 13, fontWeight: 600, color: color
            }}>
              {MONTH_NAMES[month - 1]} {year}
              {selectedMonths.length > 1 && (
                <button onClick={() => removeMonth(key)}
                  style={{ background: "none", border: "none", cursor: "pointer", color, fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>
                  ✕
                </button>
              )}
            </div>
          );
        })}

        {selectedMonths.length < 4 && (
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowPicker(p => !p)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "#f8fafc", border: "1.5px dashed #cbd5e1",
                borderRadius: 20, padding: "4px 12px", fontSize: 13,
                color: "#64748b", cursor: "pointer", fontWeight: 500
              }}>
              + Agregar mes
            </button>
            {showPicker && (
              <div style={{
                position: "absolute", top: 36, left: 0, zIndex: 100,
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                padding: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                display: "flex", flexDirection: "column", gap: 8, minWidth: 200
              }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <select value={pickerMonth} onChange={e => setPickerMonth(Number(e.target.value))}
                    style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 6px", fontSize: 12, outline: "none" }}>
                    {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={pickerYear} onChange={e => setPickerYear(Number(e.target.value))}
                    style={{ width: 72, border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 6px", fontSize: 12, outline: "none" }}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <button onClick={addMonth}
                  disabled={selectedMonths.some(m => monthKey(m.year, m.month) === monthKey(pickerYear, pickerMonth))}
                  style={{
                    background: "#6366f1", color: "#fff", border: "none", borderRadius: 6,
                    padding: "6px 0", fontSize: 12, fontWeight: 700, cursor: "pointer",
                    opacity: selectedMonths.some(m => monthKey(m.year, m.month) === monthKey(pickerYear, pickerMonth)) ? 0.5 : 1
                  }}>
                  Agregar
                </button>
                <button onClick={() => setShowPicker(false)}
                  style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
          Cargando datos...
        </div>
      ) : (
        <>
          {/* Month summary cards */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            {monthKeys.map((mk, i) => {
              const m = data.months[mk];
              return (
                <div key={mk} style={{
                  background: "#f8fafc", borderRadius: 10, padding: "10px 16px",
                  borderLeft: `3px solid ${COLORS[i]}`, flex: "1 1 140px"
                }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", textTransform: "capitalize" }}>{mk}</p>
                  <p style={{ margin: "4px 0 2px", fontSize: 16, fontWeight: 700, color: COLORS[i] }}>
                    ${m.total_month_usd.toLocaleString("en-US")}
                  </p>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {m.peak_day && <span style={{ fontSize: 10, color: "#94a3b8" }}>↑ Pico: día {m.peak_day}</span>}
                    {i === 0 && varPct !== null && (
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        color: varPct >= 0 ? "#10b981" : "#f87171",
                        background: varPct >= 0 ? "#064e3b22" : "#7f1d1d22",
                        padding: "1px 5px", borderRadius: 4
                      }}>
                        {varPct >= 0 ? "+" : ""}{varPct}% vs anterior
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false}
                tickFormatter={v => `$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} width={48} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8", paddingTop: 10 }} />
              {peakDay && (
                <ReferenceLine x={peakDay} stroke="#6366f1" strokeDasharray="4 4" opacity={0.5}
                  label={{ value: "Pico", position: "insideTopRight", fill: "#6366f1", fontSize: 10 }} />
              )}
              {monthKeys.map((mk, i) => (
                <Line key={mk} type="monotone" dataKey={mk} name={mk}
                  stroke={COLORS[i]} strokeWidth={i === 0 ? 2.5 : 1.5}
                  dot={false} activeDot={{ r: 5 }}
                  opacity={i === 0 ? 1 : 0.65} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>

          {/* Projection card */}
          {projection && (() => {
            const fmt = v => "$" + Math.round(v).toLocaleString("en-US");
            const currentColor = COLORS[monthKeys.indexOf(currentMonthKey)] || "#6366f1";
            const diffPct = Math.round(((projection.projectedTotal - projection.currentTotal) / projection.currentTotal) * 100);
            return (
              <div style={{
                marginTop: 16, border: `1.5px dashed ${currentColor}`,
                borderRadius: 12, padding: "12px 16px",
                background: currentColor + "08",
                display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center"
              }}>
                <div style={{ minWidth: 90 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    🔮 Proyección
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 10, color: "#94a3b8" }}>
                    {MONTH_NAMES[todayMonth - 1]} {todayYear}
                  </p>
                </div>
                <div style={{ flex: "0 0 auto" }}>
                  <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: currentColor }}>
                    {fmt(projection.projectedTotal)}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 10, color: "#94a3b8" }}>
                    Prom. {fmt(projection.dailyAvg)}/día
                  </p>
                </div>
                <div style={{ width: 1, height: 40, background: "#e2e8f0", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: "#64748b" }}>
                      Lleva <strong>{fmt(projection.currentTotal)}</strong> en {projection.daysElapsed} días
                    </span>
                    <span style={{ fontSize: 11, color: currentColor, fontWeight: 700 }}>
                      {projection.progress}% del mes
                    </span>
                  </div>
                  <div style={{ height: 6, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", width: `${projection.progress}%`,
                      background: `linear-gradient(90deg, ${currentColor}99, ${currentColor})`,
                      borderRadius: 99, transition: "width 0.6s ease"
                    }} />
                  </div>
                  <p style={{ margin: "5px 0 0", fontSize: 10, color: "#94a3b8" }}>
                    Promedio diario × {projection.daysInMonth} días • faltan {projection.daysInMonth - projection.daysElapsed} días
                  </p>
                  {projection.adjAmount > 0 && (
                    <p style={{ margin: "3px 0 0", fontSize: 10, color: "#f59e0b" }}>
                      ⚠ +${Math.round(projection.adjAmount).toLocaleString("en-US")} estimados —{" "}
                      {projection.adjDetails.map(a => `${a.store_name} (${a.missing_days_count}d)`).join(", ")}
                    </p>
                  )}
                </div>
                <div style={{
                  background: diffPct >= 0 ? "#ecfdf5" : "#fef2f2",
                  border: `1px solid ${diffPct >= 0 ? "#86efac" : "#fca5a5"}`,
                  borderRadius: 8, padding: "6px 12px", textAlign: "center", flexShrink: 0
                }}>
                  <p style={{ margin: 0, fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Proyectado</p>
                  <p style={{ margin: "2px 0 0", fontSize: 14, fontWeight: 800, color: diffPct >= 0 ? "#16a34a" : "#dc2626" }}>
                    {diffPct >= 0 ? "+" : ""}{diffPct}%
                  </p>
                  <p style={{ margin: "1px 0 0", fontSize: 9, color: "#94a3b8" }}>vs hoy</p>
                </div>
              </div>
            );
          })()}

          {peakDay && firstMonth?.days?.[peakDay] && (
            <div style={{
              marginTop: 16, background: "#f8fafc", borderRadius: 10,
              padding: "10px 14px", borderLeft: "3px solid #6366f1"
            }}>
              <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                💡 <strong style={{ color: "#c7d2fe" }}>Insight:</strong>{" "}
                El día {peakDay} fue el más movido de {firstKey} con ${firstMonth.days[peakDay].total_usd.toLocaleString("en-US")} en ventas.
                {varPct !== null && (varPct >= 0
                  ? ` Las ventas del mes subieron ${varPct}% respecto al mes anterior.`
                  : ` Las ventas del mes bajaron ${Math.abs(varPct)}% respecto al mes anterior.`
                )}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
